import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createDebounce from 'redux-debounced';
import createSagaMiddleware from 'redux-saga';

import { reducers, rootSaga } from 'darwin_core';

// The full darwin_core reducer map, mapped exactly as the mobile app wires it.
// Several selectors read these slices unguarded, so the web store must provide
// all of them or those selectors crash — we deliberately reuse the whole map
// rather than trimming it. Keys mirror app/src/store.js.
const reducer = {
  auth: reducers.auth,
  authSensitive: reducers.authSensitive,
  deviceRegistration: reducers.deviceRegistration,
  gatewayManagement: reducers.gatewayManagement,
  zone: reducers.zone,
  homeList: reducers.homeList,
  schedule: reducers.schedule,
  draftSchedule: reducers.draftSchedule,
  deviceGeoStatus: reducers.deviceGeoStatus,
  pendingCustomReducer: reducers.pendingCustomReducer,
  siRegistration: reducers.siRegistration,
  pendingSetpoint: reducers.pendingSetpoint,
  pendingMode: reducers.pendingMode,
  pendingHolidayMode: reducers.pendingHolidayMode,
  pendingSchedule: reducers.pendingSchedule,
  awaitingZoneStatus: reducers.awaitingZoneStatus,
  pendingPublish: reducers.pendingPublish,
  settings: reducers.settingsReducer,
  completedMessages: reducers.completedMessages,
  notifications: reducers.notificationsReducer,
  modeChangeTracker: reducers.modeChangeTracker,
  resumeRegistration: reducers.resumeRegistration,
  deviceService: reducers.deviceService,
  pendingDsrCommands: reducers.pendingDsrCommandsReducer,
  dsr: reducers.dsr,
  // Shared Desired State (SDS): the cross-client overlay of in-flight goals + its
  // settling buffer, and this client's own requests. Folded into getMode/getSetpoint
  // via getSharedPending, so once these slices exist the read model reflects goals
  // set by other clients (phone/watch) and self-heals as reported state catches up.
  // The desiredStateSettling saga (already in rootSaga) GCs the overlay.
  desiredState: reducers.desiredState,
  ownDesiredRequests: reducers.ownDesiredRequests,
  partnerAccessRequests: reducers.partnerAccessRequestsReducer,
  haloNotificationMessages: reducers.haloNotificationMessagesReducer,
};

// Action payloads thread a `sendWsMessage` function and callbacks through redux
// (invoked in sagas), and some pending slices hold non-serialisable requests, so
// the serialisability check must ignore those exact paths — same as the app.
const serializableCheck = {
  ignoredActionPaths: ['sendWsMessage', 'callback', 'data.callback'],
  ignoredPaths: [
    'schedule.scheduleRequest.sendWsMessage',
    'pendingSchedule.requests',
  ],
};

// Web session actions that drive both the reset and the write-guard below.
// AUTHENTICATED is dispatched when the user signs in (opens the guard); AUTH_RESET
// when they sign out (resets every slice and closes the guard). They are web-only
// control actions — darwin_core neither emits nor reads them.
export const AUTHENTICATED = 'web/auth/AUTHENTICATED';

// Root reset action. Dispatched when the user de-authenticates so that no
// darwin_core slice keeps the previous user's device/zone/schedule state on a
// shared browser (SECURITY). The running sagas are left untouched — they are
// idle watchers that simply re-seed on the next sign-in.
export const AUTH_RESET = 'web/auth/RESET';

// combineReducers turns the slice map into a single root reducer; wrapping it
// lets the reset action re-initialise every slice at once. Passing `undefined`
// state makes combineReducers hand each slice reducer its own initial state
// (Redux's documented store-reset pattern). All other actions flow straight
// through, so normal dispatches are unaffected.
const combinedReducer = combineReducers(reducer);

function rootReducer(state, action) {
  if (action.type === AUTH_RESET) {
    return combinedReducer(undefined, action);
  }
  return combinedReducer(state, action);
}

// Session write-guard (SECURITY). AUTH_RESET wipes the store on sign-out, but a
// write already in flight can land AFTER it and silently repopulate the store:
//   - a REST hello (jsonApi.findAll('home')) that resolves post-sign-out, and
//   - any darwin_core dispatch a saga fires off the back of a late frame.
// Enumerating every device-data action to block would be drift-prone, so this
// default-denies instead: while signed out it drops every dispatch except the
// two web-auth control actions that toggle the guard and Redux's own internal
// `@@` actions (store init/replace). On the web the darwin_core store only ever
// holds signed-in device/home/zone/schedule state, so nothing legitimate needs to
// write to it while signed out — and thunks (functions) are handled by the thunk
// middleware ahead of this, so only the plain actions they emit are gated.
function createSessionWriteGuard() {
  let signedIn = false;
  return () => (next) => (action) => {
    const type = action?.type;
    if (type === AUTHENTICATED) {
      signedIn = true;
      return next(action);
    }
    if (type === AUTH_RESET) {
      signedIn = false;
      return next(action);
    }
    if (signedIn || (typeof type === 'string' && type.startsWith('@@'))) {
      return next(action);
    }
    // Signed out: drop the write so it cannot repopulate the store.
    return undefined;
  };
}

export function createStore() {
  const sagaMiddleware = createSagaMiddleware();
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck }).concat(
        // Ahead of debounce + saga so a dropped write never reaches either.
        createSessionWriteGuard(),
        createDebounce(),
        sagaMiddleware,
      ),
  });
  store.runSaga = () => sagaMiddleware.run(rootSaga);
  return store;
}

const store = createStore();
store.runSaga();

export default store;
